import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { Input, Radio } from 'components/FormFields';
import FormModal, { FormGroup } from 'components/FormModal';
import Toaster from 'components/Toaster';
import { Icon, tokens, orange, inkA } from 'components/v3';
import { TOPIC_ICONS } from '../../LearningHub/topicIcons';

const iconGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '8px',
  marginTop: '8px',
};

const TopicFormModal = ({ topic, onSave, onClose }) => {
  const theme = useTheme();
  const t = tokens(theme);

  const [formData, setFormData] = useState(
    topic || {
      name: '',
      slug: '',
      description: '',
      mode: 'discover',
      icon: 'sparkles',
    }
  );

  const [errors, setErrors] = useState({});
  const [toaster, setToaster] = useState({ isOpen: false });

  // Auto-generate slug from name
  useEffect(() => {
    if (!topic && formData.name) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.name, topic]);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = 'Name is required';
    if (!formData.description.trim())
      next.description = 'Description is required';
    return next;
  };

  const handleSubmit = () => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Validation Error',
        message: Object.values(next)[0],
      });
      return;
    }

    onSave(formData);
  };

  const iconBoxStyle = (isSelected) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '10px 4px',
    borderRadius: '8px',
    border: isSelected
      ? `2px solid ${t.primary}`
      : `2px solid ${inkA(theme, 0.12)}`,
    background: isSelected ? orange(0.06) : 'transparent',
    color: isSelected ? t.primary : t.sub,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
  });

  const iconLabelStyle = {
    fontSize: '10px',
    color: t.sub,
    textAlign: 'center',
    lineHeight: 1.2,
  };

  return (
    <>
      <FormModal
        isOpen={true}
        onClose={onClose}
        title={topic ? 'Edit Topic' : 'Add New Topic'}
        subtitle="Organize videos into categories"
        submitText={topic ? 'Save Changes' : 'Add Topic'}
        onSubmit={handleSubmit}
        isSubmitting={false}
      >
        <FormGroup>
          <Radio
            name="mode"
            label="Mode *"
            value={formData.mode}
            onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
            inline
            options={[
              { label: 'Ideas & Tips', value: 'discover' },
              { label: 'How To Do It', value: 'howto' },
            ]}
          />
        </FormGroup>

        <FormGroup>
          <Input
            label="Name"
            required
            id="name"
            name="name"
            value={formData.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="e.g., Getting Started"
            error={!!errors.name}
            helperText={errors.name}
          />
        </FormGroup>

        <FormGroup>
          <Input
            label="Slug"
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            placeholder="auto-generated"
            helperText="URL-friendly identifier (auto-generated from name)"
            style={{ background: inkA(theme, 0.03) }}
          />
        </FormGroup>

        <FormGroup>
          <Input
            label="Description"
            required
            id="description"
            name="description"
            value={formData.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder="Brief description..."
            error={!!errors.description}
            helperText={errors.description}
          />
        </FormGroup>

        <FormGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}
          >
            Icon
          </label>
          <div style={iconGridStyle}>
            {TOPIC_ICONS.map(({ key, icon, label }) => (
              <div
                key={key}
                style={iconBoxStyle(formData.icon === key)}
                onClick={() => setFormData({ ...formData, icon: key })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setFormData({ ...formData, icon: key });
                  }
                }}
              >
                <Icon name={icon} size={26} />
                <span style={iconLabelStyle}>{label}</span>
              </div>
            ))}
          </div>
        </FormGroup>
      </FormModal>
      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
      />
    </>
  );
};

TopicFormModal.propTypes = {
  topic: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TopicFormModal;
